import { useState } from 'react';
import {
    CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis,
    YAxis
} from 'recharts';
import { SuperAdminDashboardStats } from '../@types';

interface CompanyRegistrationChartProps {
  data: SuperAdminDashboardStats;
}

const CompanyRegistrationChart: React.FC<CompanyRegistrationChartProps> = ({ data }) => {
  const [registrationPeriod, setRegistrationPeriod] = useState<'3months' | '6months' | '1year'>('3months');

  // Get the appropriate data based on selected period
  const getChartData = () => {
    const yearData = data.companyRegistrationsByYear;

    switch (registrationPeriod) {
      case '3months':
        return yearData.slice(-3);
      case '6months':
        return yearData.slice(-6);
      case '1year':
        return yearData;
      default:
        return yearData;
    }
  };

  const chartData = getChartData();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold text-gray-800 mb-1">{label}</p>
          <p className="text-sm text-blue-600">
            {payload[0].value} companies
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Company registration over time
        </h3>
        <div className="flex gap-2">
          {(['3months', '6months', '1year'] as const).map((period) => (
            <button
              key={period}
              onClick={() => setRegistrationPeriod(period)}
              className={`px-3 py-1 text-sm rounded ${
                registrationPeriod === period
                  ? "bg-blue-100 text-blue-600 border border-blue-600"
                  : "bg-white text-gray-600 border border-gray-300"
              }`}
            >
              {period === '3months' ? '3 months' : period === '6months' ? '6 months' : '1 year'}
            </button>
          ))}
        </div>
      </div>

      {/* Recharts Line Chart */}
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="month"
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fill: '#6b7280', fontSize: 12 }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="companyCount"
            stroke="#0ea5e9"
            strokeWidth={2}
            dot={{ fill: 'white', stroke: '#0ea5e9', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>

      <div className="flex items-center gap-2 text-sm text-blue-600 mb-4 mt-4">
        <div className="w-2 h-2 rounded-full bg-blue-600" />
        <span>New companies</span>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-500 mb-1">Total in period</p>
          <p className="text-2xl font-bold text-blue-600">
            {data.totalCompaniesInPeriod} companies
          </p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4">
          <p className="text-sm text-gray-500 mb-1">Avg. per month</p>
          <p className="text-2xl font-bold text-blue-600">
            {data.totalAverageCompaniesPerMonth} companies
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistrationChart;